import type {
  DimTuple,
  DrillCoordinate,
  PivotView,
  ReportSpec,
  ShowAsMode,
} from '@clockwise/semantic-core';
import { cellCoordinate, showAsFor } from '@clockwise/semantic-core';
import type { PopupAnchor } from './anchored';
import type { ChartPoint, ChartSpec } from './chart-spec';

/**
 * One clicked figure, as the table hands it to whoever offered to drill.
 *
 * The COORDINATE is the whole cell (D-20): its row tuple and its column tuple,
 * each as far as the cell knows them — a subtotal knows the outer levels, a
 * total band none of the columns. The host decides where it may land.
 */
export interface DrillCell {
  at: DrillCoordinate;
  /** What the reader saw at each pinned level, by dimension — for words, never for filters. */
  labels: Record<string, string>;
  /** The measure of the column clicked, on the model the table was drawn from. */
  measureId: string;
  /** The figure as the reader saw it, formatted — the tray repeats it. */
  text: string;
  /**
   * How the figure was SHOWN, when not as itself — `% of row` and the like.
   * The drill is into the measure either way; this is what lets the tray say
   * the number it repeats is a share, not the total its rows add up to.
   */
  showAs?: Exclude<ShowAsMode, 'raw'>;
  /** Where to open the menu: the cell, measured fresh each time. */
  anchor: PopupAnchor;
  /** Where focus goes back to when the menu closes. */
  returnFocus: HTMLElement | null;
}

export type OnDrill = (cell: DrillCell) => void;

/** What the reader saw at each pinned level — the words for a coordinate. */
export function coordinateLabels(
  spec: Pick<ReportSpec, 'rows' | 'cols'>,
  rowKey: DimTuple | undefined,
  colKey: DimTuple | undefined,
): Record<string, string> {
  const out: Record<string, string> = {};
  const add = (dims: readonly string[], tuple: DimTuple | undefined) => {
    for (const [i, value] of (tuple ?? []).entries()) {
      const id = dims[i];
      if (id) out[id] = value.label ?? value.key ?? 'no value';
    }
  };
  add(spec.rows, rowKey);
  add(spec.cols, colKey);
  return out;
}

/**
 * A click on a chart, as the SAME cell a click in its table would be.
 *
 * A bar segment or a pie slice names both coordinates: the category is a row
 * tuple, the series a column tuple and a measure. A click in the plot area of a
 * line chart names only the category — a column of lines, not one — and that is
 * the chart's row-total cell: the rows pinned, the columns free (see
 * `ChartPoint`), on the first measure drawn — the one the axis reads — and its
 * figure is that row total, as the table's total column prints it.
 *
 * `null` for a point with no number behind it: a missing value is not a figure
 * anybody can drill into.
 */
export function chartDrillCell(
  spec: Pick<ReportSpec, 'rows' | 'cols' | 'showAs'>,
  chart: ChartSpec,
  point: ChartPoint,
  where: { anchor: PopupAnchor; returnFocus: HTMLElement | null },
  view: Pick<PivotView, 'rowTotal'>,
): DrillCell | null {
  const category = chart.categories[point.category];
  if (!category) return null;
  const series = point.series !== undefined ? chart.series[point.series] : undefined;
  const measureId = series?.measureId ?? chart.series[0]?.measureId;
  if (!measureId) return null;
  const value = series
    ? (series.points[point.category] ?? null)
    : category.rowKey
      ? view.rowTotal(category.rowKey, measureId)
      : null;
  if (value === null) return null;
  const format = series?.format ?? chart.series[0]?.format ?? chart.format;
  const mode = showAsFor(spec, measureId);
  return {
    at: cellCoordinate(spec, category.rowKey, series?.colKey),
    labels: coordinateLabels(spec, category.rowKey, series?.colKey),
    measureId,
    text: format(value),
    ...(mode === 'raw' ? {} : { showAs: mode }),
    anchor: where.anchor,
    returnFocus: where.returnFocus,
  };
}
